import { RefObject, useEffect } from "react";
import { useMutation } from "@apollo/client";
import {
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { getFragmentData, gql } from "__generated__/gql";
import { ReceivedEmailMessageCategoryType } from "__generated__/gql/graphql";
import EmptyPlaceholder from "components/Shared/EmptyPlaceholder";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import RadioOptionsSelect from "components/Shared/Forms/RadioOptionsSelect";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import ReceivedEmailCategoryTag from "components/Shared/ReceivedEmailCategoryTag";
import { assertPresence } from "utils/assertions";
import { useAdminOrBrandContext } from "utils/apollo/hooks/adminOrBrand";
import { RECEIVED_EMAIL_CATEGORY_VALUES } from "utils/receivedEmailCategories";
import UserError from "utils/UserError";

const FORM_ID = "change-category-form";

const resultFragment = gql(/* GraphQL */ `
  fragment UpdateCategoryAssignmentCacheUpdate on ReceivedEmailMessageCategory {
    receivedEmailMessageId
    category
    isAutoAssociated
    receivedEmailMessage {
      emailMessageId
      receivedEmailMessageCategory {
        receivedEmailMessageId
        category
        isAutoAssociated
      }
    }
  }
`);

const createMutation = gql(/* GraphQL */ `
  mutation CreateEmailCategory(
    $emailId: UUID!
    $category: ReceivedEmailMessageCategoryType!
  ) {
    createReceivedEmailMessageCategory(
      input: {
        receivedEmailMessageCategory: {
          receivedEmailMessageId: $emailId
          category: $category
          isAutoAssociated: false
        }
      }
    ) {
      receivedEmailMessageCategory {
        ...UpdateCategoryAssignmentCacheUpdate
      }
    }
  }
`);

const updateMutation = gql(/* GraphQL */ `
  mutation UpdateEmailCategory(
    $emailId: UUID!
    $category: ReceivedEmailMessageCategoryType!
  ) {
    updateReceivedEmailMessageCategory(
      input: {
        receivedEmailMessageId: $emailId
        patch: { category: $category, isAutoAssociated: false }
      }
    ) {
      receivedEmailMessageCategory {
        ...UpdateCategoryAssignmentCacheUpdate
      }
    }
  }
`);

const deleteMutation = gql(/* GraphQL */ `
  mutation DeleteEmailCategory($emailId: UUID!) {
    deleteReceivedEmailMessageCategory(
      input: { receivedEmailMessageId: $emailId }
    ) {
      receivedEmailMessageCategory {
        ...UpdateCategoryAssignmentCacheUpdate
      }
    }
  }
`);

const CATEGORY_OPTIONS: readonly (ReceivedEmailMessageCategoryType | null)[] = [
  null,
  ...RECEIVED_EMAIL_CATEGORY_VALUES,
];

type FormFields = {
  category: ReceivedEmailMessageCategoryType | null;
};

const { ControlledFormField } = createFormFieldComponents<FormFields>();

type Props = {
  cancelRef: RefObject<HTMLButtonElement>;
  currentCategory: ReceivedEmailMessageCategoryType | null;
  emailId: UUID | null | undefined;
  hasAssociation: boolean;
  isAdmin?: boolean;
  onClose: () => void;
};

export default function CategoryChangeDialogContent({
  cancelRef,
  currentCategory,
  emailId,
  hasAssociation,
  isAdmin,
  onClose,
}: Props) {
  const formContext = useForm<FormFields>({
    defaultValues: { category: currentCategory || null },
  });

  useEffect(() => {
    formContext.reset({ category: currentCategory || null });
  }, [formContext, currentCategory]);

  const [context, isContextReady] = useAdminOrBrandContext(isAdmin);

  const [performCreate] = useMutation(createMutation, { context });
  const [performUpdate] = useMutation(updateMutation, { context });
  const [performDelete] = useMutation(deleteMutation, { context });

  const [handleValidSubmit, { isLoading: isSaving }] =
    useCreateValidSubmitHandler(
      async (data: FormFields) => {
        assertPresence(emailId);

        const nextCategory = data.category;

        if (!nextCategory) {
          if (!hasAssociation) {
            throw new UserError("No associated category to remove");
          }

          const result = await performDelete({ variables: { emailId } });
          const updatedRemc = getFragmentData(
            resultFragment,
            result.data?.deleteReceivedEmailMessageCategory
              ?.receivedEmailMessageCategory,
          );
          assertPresence(updatedRemc);
          return updatedRemc;
        }

        const updateResult = await (async () => {
          if (hasAssociation) {
            const result = await performUpdate({
              variables: { emailId, category: nextCategory },
            });
            return result.data?.updateReceivedEmailMessageCategory
              ?.receivedEmailMessageCategory;
          }

          const result = await performCreate({
            variables: { emailId, category: nextCategory },
          });
          return result.data?.createReceivedEmailMessageCategory
            ?.receivedEmailMessageCategory;
        })();

        const updatedRemc = getFragmentData(resultFragment, updateResult);

        assertPresence(updatedRemc);

        return updatedRemc;
      },
      {
        getSuccessToast: (result) => ({
          title: result?.receivedEmailMessage?.receivedEmailMessageCategory
            ? `Category ${hasAssociation ? "updated" : "assigned"}`
            : "Category removed",
        }),
        onUpdateComplete: () => {
          onClose();
        },
      },
    );

  return (
    <AlertDialogContent>
      <AlertDialogHeader>
        {hasAssociation ? "Change" : "Assign"} Email Category
      </AlertDialogHeader>
      <AlertDialogBody>
        <Form
          formContext={formContext}
          onValidSubmit={handleValidSubmit}
          id={FORM_ID}
        >
          <ControlledFormField
            name="category"
            renderInput={({ field }) => (
              <RadioOptionsSelect
                {...field}
                options={CATEGORY_OPTIONS}
                getLabel={(option) =>
                  option ? (
                    <ReceivedEmailCategoryTag category={option} />
                  ) : (
                    <EmptyPlaceholder>Uncategorized</EmptyPlaceholder>
                  )
                }
                isDisabled={!isContextReady}
                sx={{
                  display: "flex",
                  flexFlow: "column",
                  gap: ".5em",
                  ".chakra-radio": {
                    alignItems: "flex-start",
                    ".chakra-radio__control": { mt: ".2em" },
                  },
                }}
              />
            )}
            label={null}
            isDisabled={!isContextReady}
            my={0}
          />
        </Form>
      </AlertDialogBody>
      <AlertDialogFooter>
        <Button ref={cancelRef} onClick={onClose} size="sm" variant="outline">
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          size="sm"
          colorScheme="pinkButton"
          ml={3}
          isLoading={isSaving}
          isDisabled={!emailId || !isContextReady}
        >
          Save
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
}
